import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Button, Icon, Card, Col, Row } from '@folio/stripes/components';
import css from './SafeDeleteInstancePage.css';

const DeletionProgress = ({
  deletionState,
  onRetry,
  onCancel,
  onReturnToInventory,
  onPause,
  onResume,
  onCancelDeletion,
  instanceTitle,
}) => {
  const {
    isDeleting,
    currentStep,
    progress,
    error,
    completedOperations,
    failedOperations = [],
    isPaused = false,
    isCancelled = false,
    batchMetrics = {},
  } = deletionState;

  const renderProgressBar = () => {
    const {
      percentage = 0,
      completed = 0,
      total = 0,
      currentBatch = 0,
      totalBatches = 0,
      processingRate = 0,
      estimatedTimeRemaining = 0,
    } = progress || {};

    const safePercentage = Math.max(0, Math.min(100, percentage || 0));

    return (
      <div className={css.progressContainer}>
        <div className={css.progressBar}>
          <div
            className={css.progressFill}
            style={{ width: `${safePercentage}%` }}
          />
        </div>
        <div className={css.progressText}>
          {safePercentage.toFixed(0)}% ({completed}/{total})
        </div>
        {/* Enhanced progress info */}
        {totalBatches > 0 && (
          <div className={css.batchInfo}>
            <FormattedMessage
              id="ui-plugin-inventory-templates.safeDelete.batchProgress"
              defaultMessage="Batch {currentBatch} of {totalBatches}"
              values={{ currentBatch, totalBatches }}
            />
          </div>
        )}
        {processingRate > 0 && (
          <div className={css.performanceInfo}>
            <FormattedMessage
              id="ui-plugin-inventory-templates.safeDelete.processingRate"
              defaultMessage="Processing: {rate} items/sec"
              values={{ rate: processingRate }}
            />
            {estimatedTimeRemaining > 0 && (
              <span className={css.estimatedTime}>
                {' • '}
                <FormattedMessage
                  id="ui-plugin-inventory-templates.safeDelete.estimatedTime"
                  defaultMessage="Est. {time}s remaining"
                  values={{ time: estimatedTimeRemaining }}
                />
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderSuccessState = () => (
    <Card
      className={css.progressCard}
      headerStart={
        <h4>
          <FormattedMessage
            id="ui-plugin-inventory-templates.safeDelete.deletionProgressTitle"
            defaultMessage="Deletion Progress"
          />
        </h4>
      }
    >
      <div className={css.progressContent}>
        <div className={css.successHeader}>
          <Icon icon="check-circle" size="large" className={css.successIcon} />
          <h3>
            <FormattedMessage
              id="ui-plugin-inventory-templates.safeDelete.deletionCompleted"
              defaultMessage="Deletion Completed Successfully"
            />
          </h3>
        </div>

        <div className={css.completedSummary}>
          <FormattedMessage
            id="ui-plugin-inventory-templates.safeDelete.deletionSummaryWithIndexInfo"
            defaultMessage="Successfully deleted instance {title} and all associated records. Please note that it may take a few moments for the search index to update."
            values={{ title: instanceTitle }}
          />
        </div>

        <div className={css.completedOperations}>
          <h4>
            <FormattedMessage
              id="ui-plugin-inventory-templates.safeDelete.completedOperations"
              defaultMessage="Completed Operations:"
            />
          </h4>
          <div className={css.operationsScrollContainer}>
            {completedOperations.map((op, index) => (
              <div key={index} className={css.operationItem}>
                <Icon icon="check" size="small" className={css.operationIcon} />
                <span>
                  <FormattedMessage
                    id={`ui-plugin-inventory-templates.safeDelete.deleted${op.type.charAt(0).toUpperCase() + op.type.slice(1)}`}
                    defaultMessage={
                      op.type === 'item'
                        ? 'Deleted item: {name}'
                        : op.type === 'holding'
                          ? 'Deleted holding: {name}'
                          : 'Deleted instance: {name}'
                    }
                    values={{ name: op.name }}
                  />
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Show failed operations if any */}
        {failedOperations.length > 0 && (
          <div className={css.failedOperations}>
            <h4 className={css.failedHeader}>
              <FormattedMessage
                id="ui-plugin-inventory-templates.safeDelete.failedOperations"
                defaultMessage="Failed Operations ({count})"
                values={{ count: failedOperations.length }}
              />
            </h4>
            <div className={css.operationsScrollContainer}>
              {failedOperations.map((op, index) => (
                <div key={index} className={css.operationItem}>
                  <Icon
                    icon="exclamation-circle"
                    size="small"
                    className={css.errorIcon}
                  />
                  <span>
                    <FormattedMessage
                      id={`ui-plugin-inventory-templates.safeDelete.failed${op.type.charAt(0).toUpperCase() + op.type.slice(1)}`}
                      defaultMessage={
                        op.type === 'item'
                          ? 'Failed to delete item: {name} - {error}'
                          : op.type === 'holding'
                            ? 'Failed to delete holding: {name} - {error}'
                            : 'Failed to delete instance: {name} - {error}'
                      }
                      values={{ name: op.name, error: op.error }}
                    />
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className={css.successFooter}>
        <Button buttonStyle="primary" onClick={onReturnToInventory}>
          <FormattedMessage
            id="ui-plugin-inventory-templates.safeDelete.returnToInventory"
            defaultMessage="Return to Inventory"
          />
        </Button>
      </div>
    </Card>
  );

  const renderErrorState = () => (
    <Card
      className={css.progressCard}
      headerStart={
        <h4>
          <FormattedMessage
            id="ui-plugin-inventory-templates.safeDelete.deletionProgressTitle"
            defaultMessage="Deletion Progress"
          />
        </h4>
      }
    >
      <div className={css.progressContent}>
        <div className={css.errorHeader}>
          <Icon
            icon="exclamation-circle"
            size="large"
            className={css.errorIcon}
          />
          <h3>
            <FormattedMessage
              id="ui-plugin-inventory-templates.safeDelete.deletionFailed"
              defaultMessage="Deletion Failed"
            />
          </h3>
        </div>

        <div className={css.errorMessage}>{error}</div>

        {completedOperations.length > 0 && (
          <div className={css.partialSuccess}>
            <h4>
              <FormattedMessage
                id="ui-plugin-inventory-templates.safeDelete.partiallyCompleted"
                defaultMessage="Partially Completed Operations:"
              />
            </h4>
            {completedOperations.map((op, index) => (
              <div key={index} className={css.operationItem}>
                <Icon icon="check" size="small" className={css.operationIcon} />
                <span>
                  <FormattedMessage
                    id={`ui-plugin-inventory-templates.safeDelete.deleted${op.type.charAt(0).toUpperCase() + op.type.slice(1)}`}
                    defaultMessage={
                      op.type === 'item'
                        ? 'Deleted item: {name}'
                        : op.type === 'holding'
                          ? 'Deleted holding: {name}'
                          : 'Deleted instance: {name}'
                    }
                    values={{ name: op.name }}
                  />
                </span>
              </div>
            ))}
          </div>
        )}

        <div className={css.actionButtons}>
          <Button buttonStyle="primary" onClick={onRetry}>
            <Icon icon="refresh" size="small" />
            <FormattedMessage
              id="ui-plugin-inventory-templates.safeDelete.retryDeletion"
              defaultMessage="Retry Deletion"
            />
          </Button>

          <Button
            buttonStyle="default"
            onClick={onCancel}
            style={{ marginLeft: '8px' }}
          >
            <FormattedMessage
              id="ui-plugin-inventory-templates.safeDelete.cancelAndReturn"
              defaultMessage="Cancel and Return to Safety Check"
            />
          </Button>
        </div>
      </div>

      <div className={css.successFooter}>
        <Button buttonStyle="primary" onClick={onReturnToInventory}>
          <FormattedMessage
            id="ui-plugin-inventory-templates.safeDelete.returnToInventory"
            defaultMessage="Return to Inventory"
          />
        </Button>
      </div>
    </Card>
  );

  const renderProgressState = () => (
    <Card
      className={css.progressCard}
      headerStart={
        <h4>
          <FormattedMessage
            id="ui-plugin-inventory-templates.safeDelete.deletionProgressTitle"
            defaultMessage="Deletion Progress"
          />
        </h4>
      }
    >
      <div className={css.progressContent}>
        <div className={css.progressHeader}>
          <Icon
            icon="spinner-ellipsis"
            size="large"
            className={css.spinnerIcon}
          />
          <h3>
            <FormattedMessage
              id="ui-plugin-inventory-templates.safeDelete.deletionInProgress"
              defaultMessage="Deletion in Progress..."
            />
          </h3>
        </div>

        <div className={css.currentStep}>{currentStep}</div>

        {renderProgressBar()}

        <div className={css.currentOperation}>{progress.currentItem}</div>

        <div className={css.warningMessage}>
          <Icon icon="exclamation-triangle" size="small" />
          <FormattedMessage
            id="ui-plugin-inventory-templates.safeDelete.doNotClose"
            defaultMessage="Please do not close this window while deletion is in progress."
          />
        </div>

        {/* Enhanced control buttons */}
        <div className={css.controlButtons}>
          {isDeleting && !isPaused && (
            <Button
              buttonStyle="default"
              onClick={onPause}
              disabled={isCancelled}
            >
              <Icon icon="pause" size="small" />
              <FormattedMessage
                id="ui-plugin-inventory-templates.safeDelete.pauseDeletion"
                defaultMessage="Pause"
              />
            </Button>
          )}

          {isPaused && (
            <Button
              buttonStyle="primary"
              onClick={onResume}
              disabled={isCancelled}
            >
              <Icon icon="play" size="small" />
              <FormattedMessage
                id="ui-plugin-inventory-templates.safeDelete.resumeDeletion"
                defaultMessage="Resume"
              />
            </Button>
          )}

          {(isDeleting || isPaused) && !isCancelled && (
            <Button
              buttonStyle="danger"
              onClick={onCancelDeletion}
              style={{ marginLeft: '8px' }}
            >
              <Icon icon="times" size="small" />
              <FormattedMessage
                id="ui-plugin-inventory-templates.safeDelete.cancelDeletion"
                defaultMessage="Cancel"
              />
            </Button>
          )}
        </div>
      </div>
    </Card>
  );

  // Don't render anything if not in deletion process
  if (!isDeleting && !error && completedOperations.length === 0) {
    return null;
  }

  return (
    <Row>
      <Col xs={12}>
        {error
          ? renderErrorState()
          : completedOperations.length > 0 && !isDeleting
            ? renderSuccessState()
            : renderProgressState()}
      </Col>
    </Row>
  );
};

DeletionProgress.propTypes = {
  deletionState: PropTypes.shape({
    isDeleting: PropTypes.bool,
    currentStep: PropTypes.string,
    progress: PropTypes.shape({
      completed: PropTypes.number,
      total: PropTypes.number,
      percentage: PropTypes.number,
      currentItem: PropTypes.string,
      currentBatch: PropTypes.number,
      totalBatches: PropTypes.number,
      processingRate: PropTypes.number,
      estimatedTimeRemaining: PropTypes.number,
    }),
    error: PropTypes.string,
    completedOperations: PropTypes.array,
    failedOperations: PropTypes.array,
    isPaused: PropTypes.bool,
    isCancelled: PropTypes.bool,
    batchMetrics: PropTypes.object,
  }).isRequired,
  onRetry: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onReturnToInventory: PropTypes.func.isRequired,
  onPause: PropTypes.func,
  onResume: PropTypes.func,
  onCancelDeletion: PropTypes.func,
  instanceTitle: PropTypes.string,
};

export default DeletionProgress;
